import type { Extensions } from '@tiptap/react'
import { useEditor } from '@tiptap/react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { getVariantClasses } from '../markdown-editor.config'
import type { EditorVariant } from '../markdown-editor.interface'
import { getMarkdownFromEditor, markdownToHtml } from '../utils/markdown.util'
import { createKeyHandler } from './useMarkdownEditorKeyHandler.hook'

export interface UseMarkdownEditorProps {
  extensions: Extensions
  initialValue: string
  variant: EditorVariant
  isReadOnly: boolean
  isDisabled: boolean
  isAutoFocus: boolean
  isSpellCheck: boolean
  onChange?: (markdown: string) => void
  onBlur?: (markdown: string) => void
  onSubmit?: (markdown: string) => void
}

/**
 * Hook that creates and manages the TipTap editor instance
 */
export const useMarkdownEditor = ({
  extensions,
  initialValue,
  variant,
  isReadOnly,
  isDisabled,
  isAutoFocus,
  isSpellCheck,
  onChange,
  onBlur,
  onSubmit,
}: UseMarkdownEditorProps) => {
  const [isMounted, setIsMounted] = useState(false)
  const isFirstMount = useRef(true)

  const editor = useEditor({
    extensions,
    content: initialValue ? markdownToHtml(initialValue) : '',
    editable: !isReadOnly && !isDisabled,
    autofocus: isAutoFocus,
    editorProps: {
      attributes: {
        class: cn('saaga-wysiwyg max-w-none focus:outline-none', getVariantClasses(variant)),
        spellcheck: isSpellCheck.toString(),
      },
      handleKeyDown: createKeyHandler({ editor: null, variant, onSubmit }),
    },
    onUpdate: ({ editor: e }) => onChange?.(getMarkdownFromEditor(e)),
    onBlur: ({ editor: e }) => onBlur?.(getMarkdownFromEditor(e)),
  })

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (editor) {
      editor.setEditable(!isReadOnly && !isDisabled)
    }
  }, [editor, isReadOnly, isDisabled])

  useEffect(() => {
    if (!editor) return

    if (isFirstMount.current) {
      isFirstMount.current = false
      return
    }

    const currentContent = getMarkdownFromEditor(editor)
    if (currentContent !== initialValue) {
      editor.commands.setContent(initialValue ? markdownToHtml(initialValue) : '')
    }
  }, [editor, initialValue])

  return { editor, isMounted }
}
